var __awaiter = this && this.__awaiter || function (thisArg, _arguments, P, generator) {
  function adopt(value) {
    return value instanceof P ? value : new P(function (resolve) {
      resolve(value);
    });
  }
  return new (P || (P = Promise))(function (resolve, reject) {
    function fulfilled(value) {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    }
    function rejected(value) {
      try {
        step(generator["throw"](value));
      } catch (e) {
        reject(e);
      }
    }
    function step(result) {
      result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected);
    }
    step((generator = generator.apply(thisArg, _arguments || [])).next());
  });
};
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Absolute, Box, Button, Flex, FormField, Label, Relative, Spinner, Text, Tooltip } from 'pcln-design-system';
import { ErrorToolTip, FormInput, SelectInput, SuccessIcon, WarningIcon } from './OtpForm.styles';
import { uniqCountryCodes } from './helpers/buildCountryDropdown';
import otpService from '../../utils/otpService';
import { useOtpContext } from '../../context/OtpContext';
import { ChatbotDataContext } from '../../context/ChatbotDataContext';
import { otpDeliveryMechanisms } from '../../constants';
const OtpForm = () => {
  const errorMessage = 'I’m having some trouble accessing your trip information. ';
  const deliveryType = otpDeliveryMechanisms.SMS;
  const [phoneColor, setPhoneColor] = useState();
  const [emailColor, setEmailColor] = useState();
  const [phoneNumber, setPhoneNumber] = useState();
  const [email, setEmail] = useState();
  const [countryCode, setCountryCode] = useState('1');
  const [otpFormSubmissionInProgress, setOtpFormSubmissionInProgress] = useState(false);
  const {
    setOtpRequestErrorMsg,
    setOtpDeliveryType,
    setVerificationApiFunction,
    setShowOtpVerificationForm,
    setOtpFormData
  } = useOtpContext();
  const {
    setAccountLessTripsAccessPayload,
    submitChatMessagesPayload,
    requestFrom
  } = useContext(ChatbotDataContext);
  const {
    cguid,
    resolvedClientIP
  } = submitChatMessagesPayload || {};
  const otpServiceInstance = useMemo(() => otpService({
    deliveryType
  }), [otpService]);
  useEffect(() => {
    if (otpServiceInstance.verify) {
      setVerificationApiFunction(() => otpServiceInstance.verify);
    }
  }, [otpServiceInstance, setVerificationApiFunction]);
  useEffect(() => {
    setOtpDeliveryType(deliveryType);
  }, [deliveryType]);
  const isPhoneValidated = useMemo(() => {
    if (!phoneNumber) return false;
    // Check if the phone number contains only digits or has the format XXX-XXX-XXXX
    const isValidFormat = /^[0-9]+$/.test(phoneNumber) || /^\d{3}-\d{3}-\d{4}$/.test(phoneNumber);
    if (!isValidFormat) return false;
    // Remove any dashes to get just the digits
    const digits = phoneNumber.replace(/-/g, '');
    // For US/Canada (country code 1), require exactly 10 digits
    if (countryCode === '1') {
      return digits.length === 10;
    }
    // For all other country codes, require 5 or more digits
    return digits.length >= 5;
  }, [phoneNumber, countryCode]);
  const isEmailValidated = useMemo(() => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }, [email]);
  const disableButton = useMemo(() => {
    return !(isEmailValidated && isPhoneValidated);
  }, [isEmailValidated, isPhoneValidated]);
  function checkPhoneValidation() {
    if (phoneNumber) {
      setPhoneColor(isPhoneValidated ? 'success' : 'error');
    }
  }
  function checkEmailValidation() {
    if (email) {
      setEmailColor(isEmailValidated ? 'success' : 'error');
    }
  }
  function handlePhoneChange(e) {
    const value = e.currentTarget.value;
    // Only allow digits
    if (/^\d*$/.test(value)) {
      setPhoneNumber(value);
    }
  }
  function handleEmailChange(e) {
    const value = e.currentTarget.value;
    setEmail(value);
  }
  function submitFormHandler(e) {
    return __awaiter(this, void 0, void 0, function* () {
      e.preventDefault();
      setOtpRequestErrorMsg('');
      setOtpFormData({
        phoneNumber: Number(phoneNumber),
        email,
        countryCode
      });
      setAccountLessTripsAccessPayload({
        phoneNumber: Number(phoneNumber),
        emailAddress: email,
        scope: 'TRIPS'
      });
      setOtpFormSubmissionInProgress(true);
      setOtpRequestErrorMsg('');
      try {
        const otpChallenge = yield otpServiceInstance.createChallenge({
          phoneNumber,
          email,
          countryCode,
          scope: 'trips',
          requestHeaders: {
            'x-pcln-client-ip': resolvedClientIP,
            cguid
          },
          requestFrom
        });
        if (otpChallenge) {
          setShowOtpVerificationForm(true);
        } else {
          setOtpRequestErrorMsg(errorMessage);
        }
      } catch (_e) {
        setOtpRequestErrorMsg(errorMessage);
      } finally {
        setOtpFormSubmissionInProgress(false);
      }
    });
  }
  return React.createElement(Box, {
    maxWidth: '390px'
  }, otpFormSubmissionInProgress ? React.createElement(Spinner, null) : React.createElement(Box, null, React.createElement(Flex, {
    flexDirection: 'column'
  }, React.createElement(Flex, {
    alignItems: 'center'
  }, React.createElement(Box, {
    width: '30%',
    mr: 2
  }, React.createElement(Relative, null, React.createElement(FormField, null, React.createElement(Label, {
    htmlFor: 'countryCodes'
  }, 'Country Code \u207A'), React.createElement(SelectInput, {
    defaultValue: '1',
    id: 'countryCodes',
    name: 'countryCodes',
    value: countryCode,
    onChange: function (e) {
      setCountryCode(e.target.value);
    }
  }, uniqCountryCodes.map(country => {
    return React.createElement("option", {
      value: country.phoneCode,
      key: country.label
    }, country.label);
  })), React.createElement(SuccessIcon, {
    color: 'success'
  })), React.createElement(Absolute, {
    bg: 'background.lightest',
    top: 17,
    left: 14,
    width: '75px',
    style: {
      pointerEvents: 'none'
    }
  }, React.createElement(Box, null, React.createElement(Text, {
    fontSize: 1
  }, "+", countryCode))))), React.createElement(Box, {
    width: '70%'
  }, React.createElement(FormField, null, React.createElement(Label, {
    htmlFor: 'phone'
  }, 'Phone Number \u207A'), React.createElement(FormInput, {
    id: 'phone',
    name: 'phone',
    type: 'text',
    inputMode: 'numeric',
    pattern: '[0-9]*',
    placeholder: 'Phone number \u207A',
    onChange: handlePhoneChange,
    onFocus: function () {
      setPhoneColor(undefined);
    },
    onBlur: checkPhoneValidation,
    color: phoneColor,
    value: phoneNumber,
    "aria-describedby": 'phone-error'
  }), phoneColor === 'error' ? React.createElement(WarningIcon, {
    color: 'error'
  }) : null, phoneColor === 'success' ? React.createElement(SuccessIcon, {
    color: 'success'
  }) : null), phoneColor === 'error' ? React.createElement(ErrorToolTip, {
    id: 'phone-error',
    right: true,
    color: 'error'
  }, "Phone number is invalid") : null)), React.createElement(Box, {
    my: 2
  }, React.createElement(FormField, null, React.createElement(Label, {
    htmlFor: 'email'
  }, 'Email Address \u207A'), React.createElement(FormInput, {
    id: 'email',
    name: 'email',
    placeholder: 'Email address \u207A',
    onChange: handleEmailChange,
    onFocus: function () {
      setEmailColor(undefined);
    },
    onBlur: checkEmailValidation,
    color: emailColor,
    value: email
  }), emailColor === 'error' ? React.createElement(WarningIcon, {
    color: 'error'
  }) : null, emailColor === 'success' ? React.createElement(SuccessIcon, {
    color: 'success'
  }) : null), emailColor === 'error' ? React.createElement(Tooltip, {
    id: 'email-error',
    right: true,
    color: 'error'
  }, "Email is invalid") : null)), React.createElement(Box, {
    mt: 1
  }, React.createElement(Button, {
    disabled: disableButton,
    onClick: submitFormHandler
  }, "Continue"))));
};
export default OtpForm;